import { colors } from "@hortis/ui/colors";
import { toast } from "@hortis/ui/sonner";
import type MapboxDraw from "@mapbox/mapbox-gl-draw";
import { pipe } from "@mobily/ts-belt";
import {
  useChildMatches,
  useNavigate,
  useSearch,
} from "@tanstack/react-router";
import * as turf from "@turf/turf";
import {
  GeoJsonFeatureType,
  LocationFieldsFragment,
} from "generated/graphqlSDK";
import type { Feature, MultiPolygon, Polygon } from "geojson";
import { GeoJSONFeature } from "maplibre-gl";
import {
  type MutableRefObject,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { queryClient } from "src/app";
import { parseLocationGeometry } from "src/components/map/location-bed-polygons/parse-geometry";
import type { CoordinateMenuProps } from "src/components/map/material-clusters/coordinate-menu";
import { useGqlMutation } from "src/utils/hooks/graphql";
import { useMobile } from "src/utils/hooks/media-query";
import { truncateString } from "src/utils/truncate-string";
import { match } from "ts-pattern";
import {
  GetCollectionGeometryQuery,
  type MaterialFieldsFragment,
  namedOperations,
  useUpdateCollectionSiteMutation,
  useUpdateLocationMutation,
} from "../../../generated/graphql";
import { useMaplibre } from "../../components/map/utils";
import {
  createMaterialNumber,
  parseMaterialNumber,
} from "../collection/components/plant-materials/material-number";
import { updatePlantMaterialDocument } from "../records/material/graphql";
import { useMapOptionsStore } from "./components/map-options-store";
import type {
  MapMode,
  MapPositioningLocation,
  MapPositioningMaterial,
} from "./components/types";
import { useMapAttribution } from "./components/use-map-attribution";
import { useMapPaste } from "./components/use-map-paste";
import { useMapResize } from "./components/use-map-resize";
import { bbox, mapId } from "./utils";

const EDITING_ZOOM = 19;

type State = {
  sidebarOpen: boolean;
  filtersOpen: boolean;
  newMaterialDrawerOpen: boolean;
  mode: MapMode;
  editingMaterial: MapPositioningMaterial | null;
  editingLocation: MapPositioningLocation | null;
  coordinateMenuProps: CoordinateMenuProps | null;
  newMaterialCoordinates: Pick<
    CoordinateMenuProps,
    "latitude" | "longitude"
  > | null;
};

export const useMapRouteMatch = () => {
  const matched = useChildMatches();
  const route = matched[0];

  return useMemo(
    () =>
      match(route)
        .with(
          { routeId: "/_layout/site/$siteSlug/map/material/$materialNumber" },
          (route) => {
            return {
              ...route,
              route: "material",
              number:
                "materialNumber" in route.params
                  ? parseMaterialNumber(route.params.materialNumber)
                  : undefined,
            } as const;
          },
        )
        .with({ routeId: "/_layout/site/$siteSlug/map/materials" }, (route) => {
          return {
            ...route,
            route: "materials-list",
          } as const;
        })
        .with(
          { routeId: "/_layout/site/$siteSlug/map/location/$locationName" },
          (route) => {
            return {
              ...route,
              route: "location",
              name:
                "locationName" in route.params
                  ? route.params.locationName
                  : undefined,
            } as const;
          },
        )
        .with({ routeId: "/_layout/site/$siteSlug/map/locations" }, (route) => {
          return {
            ...route,
            route: "locations-list",
          } as const;
        })
        .otherwise(() => ({ route: "map" }) as const),
    [route],
  );
};

export const useMapStore = ({
  refetchSite,
  drawRef,
  refetchLocationGeometry,
  geometryQueryVariables,
}: {
  refetchSite: () => void;
  drawRef: MutableRefObject<MapboxDraw | undefined>;
  refetchLocationGeometry: () => void;
  geometryQueryVariables: Object;
}) => {
  const isMobile = useMobile();
  const [initialized, setInitialized] = useState(false);
  const { [mapId]: map } = useMaplibre();
  const navigate = useNavigate({ from: "/site/$siteSlug/map" });
  const matchedRoute = useMapRouteMatch();
  const updateMaterial = useGqlMutation({
    mutation: updatePlantMaterialDocument,
    onError: (err) => {
      toast.error("Failed to update position");
    },
    onSuccess: (res) => {
      if (res.updatePlantMaterial?.__typename !== "PlantMaterial") {
        toast.error("Failed to update position");
        return;
      }
      const material = res.updatePlantMaterial;

      if (
        material.accession?.scientificName != null &&
        material.position != null
      ) {
        const updatedFeature = {
          type: GeoJsonFeatureType.Feature,
          geometry: {
            type: "Point" as const,
            coordinates: [
              material.position.longitude,
              material.position.latitude,
            ],
          },
          properties: {
            id: material.id,
            adHoc: {
              id: material.id,
              qualifier: material.qualifier,
              scientificName: material.accession.scientificName,
              accessionNumber: material.accession.accessionNumber,
              materialNumber: createMaterialNumber(
                material.accession.accessionNumber,
                material.qualifier,
                "/",
              ),
            },
          },
        };
        void queryClient.setQueryData(
          [namedOperations.Query.GetCollectionGeometry, geometryQueryVariables],
          (cachedData: GetCollectionGeometryQuery | undefined) => {
            if (cachedData?.org?.site?.plantMaterialsAsGeoJSON == null) {
              return cachedData;
            }
            const geoJson = JSON.parse(
              cachedData.org?.site?.plantMaterialsAsGeoJSON,
            );
            const features = geoJson.features?.map((feature: GeoJSONFeature) =>
              feature.properties?.id === material.id ? updatedFeature : feature,
            );
            return {
              org: {
                ...cachedData.org,
                site: {
                  ...cachedData.org?.site,
                  plantMaterialsAsGeoJSON: JSON.stringify({
                    ...geoJson,
                    features,
                  }),
                },
              },
            };
          },
        );
      }
      toast.success("Position updated");
      void queryClient.invalidateQueries({
        queryKey: [namedOperations.Query.GetMaterial],
      });
      void queryClient.invalidateQueries({
        queryKey: [namedOperations.Query.GetMaterialById],
      });
      void queryClient.invalidateQueries({
        queryKey: [namedOperations.Query.PlantMaterials],
      });
      void queryClient.invalidateQueries({
        queryKey: [namedOperations.Query.MapListMaterials],
      });
    },
  });
  const [_, updateSite] = useUpdateCollectionSiteMutation();
  const [___, updateLocation] = useUpdateLocationMutation();
  const { setLocationPolygonsEnabled } = useMapOptionsStore();
  const search = useSearch({
    from: "/_layout/site/$siteSlug/map",
  });
  const [shownNoInitialView, setShownNoInitialView] = useState(false);
  const [state, setState] = useState<State>({
    sidebarOpen: true,
    filtersOpen: false,
    newMaterialDrawerOpen: false,
    mode: "view",
    editingMaterial: null,
    coordinateMenuProps: null,
    newMaterialCoordinates: null,
    editingLocation: null,
  });

  const collapseSidebar = useCallback(() => {
    setState((state) => ({ ...state, sidebarOpen: false }));
  }, []);

  const expandSidebar = useCallback(() => {
    setState((state) => ({ ...state, sidebarOpen: true }));
  }, []);

  const cancelEdit = useCallback(() => {
    setState((state) => ({ ...state, editingMaterial: null, mode: "view" }));
  }, []);

  const cancelEditLocation = useCallback(() => {
    setState((state) => ({ ...state, editingLocation: null, mode: "view" }));
  }, []);

  const openFilters = useCallback(() => {
    setState((state) => ({ ...state, filtersOpen: true }));
  }, []);

  const closeFilters = useCallback(() => {
    setState((state) => ({ ...state, filtersOpen: false }));
  }, []);

  const openNewMaterial = useCallback(() => {
    setState((state) => ({
      ...state,
      newMaterialDrawerOpen: true,
      newMaterialCoordinates: state.coordinateMenuProps,
    }));
  }, []);

  const confirmNewMaterial = useCallback(
    async (material: MaterialFieldsFragment) => {
      if (
        material.position != null &&
        material.accession?.scientificName != null
      ) {
        const newFeature = {
          type: GeoJsonFeatureType.Feature,
          geometry: {
            type: "Point" as const,
            coordinates: [
              material.position.longitude,
              material.position.latitude,
            ],
          },
          properties: {
            id: material.id,
            adHoc: {
              id: material.id,
              qualifier: material.qualifier,
              scientificName: material.accession.scientificName,
              accessionNumber: material.accession.accessionNumber,
              materialNumber: createMaterialNumber(
                material.accession.accessionNumber,
                material.qualifier,
                "/",
              ),
            },
          },
        };
        void queryClient.setQueryData(
          [namedOperations.Query.GetCollectionGeometry, geometryQueryVariables],
          (cachedData: GetCollectionGeometryQuery | undefined) => {
            if (cachedData?.org?.site?.plantMaterialsAsGeoJSON == null) {
              return cachedData;
            }
            const geoJson = JSON.parse(
              cachedData.org?.site?.plantMaterialsAsGeoJSON,
            );
            return {
              org: {
                ...cachedData.org,
                site: {
                  ...cachedData.org?.site,
                  plantMaterialsAsGeoJSON: JSON.stringify({
                    ...geoJson,
                    features: [...geoJson.features, newFeature],
                  }),
                },
              },
            };
          },
        );
        await navigate({
          to: "/site/$siteSlug/map/material/$materialNumber",
          params: {
            materialNumber: createMaterialNumber(
              material.accession.accessionNumber,
              material.qualifier,
            ),
          },
        });
      }
      setState((state) => ({
        ...state,
        mode: "view",
        newMaterialDrawerOpen: false,
        coordinateMenuProps: null,
      }));
    },
    [navigate],
  );

  const cancelNewMaterial = useCallback(() => {
    setState((state) => ({
      ...state,
      newMaterialDrawerOpen: false,
      coordinateMenuProps: null,
    }));
  }, []);

  const requestEdit = useCallback(
    (material: MapPositioningMaterial) => {
      if (material.position == null) {
        map?.flyTo({
          zoom: EDITING_ZOOM,
          padding: { left: 0, top: 0, right: 0, bottom: 0 },
        });
      } else {
        map?.flyTo({
          center: {
            lat: material.position.latitude,
            lng: material.position.longitude,
          },
          zoom: EDITING_ZOOM,
          padding: { left: 0, top: 0, right: 0, bottom: 0 },
        });
      }

      setState((state) => ({
        ...state,
        mode: "editMaterial",
        editingMaterial: material,
      }));
    },
    [map],
  );
  const requestLocationEdit = useCallback(
    (location: LocationFieldsFragment) => {
      setLocationPolygonsEnabled(true);
      const parsedLocationGeometry = parseLocationGeometry(location.geometry);
      const parsedLocation = {
        ...location,
        geometry: parsedLocationGeometry,
      };
      const parsedMultiPolygon = parsedLocation.geometry;

      if (parsedMultiPolygon == null) {
        map?.flyTo({
          zoom: EDITING_ZOOM,
          padding: 20,
        });
      } else {
        map?.fitBounds(pipe(parsedMultiPolygon, turf.flatten, bbox), {
          padding: 30,
        });
      }

      setState((state) => ({
        ...state,
        mode: "editLocation",
        editingLocation: parsedLocation,
      }));
    },
    [map, setLocationPolygonsEnabled],
  );

  // eslint-disable-next-line sonarjs/cognitive-complexity
  const confirmEditLocationGeometry = useCallback(async () => {
    if (map && state.editingLocation != null && drawRef.current != null) {
      const featureCollection = drawRef.current.getAll();

      if (featureCollection.features.length === 0) {
        /**
         * There are no features which means we have not drawn anything or all the polygons have been deleted
         */

        const { data } = await updateLocation({
          id: state.editingLocation.id,
          input: {
            geometry: null,
            geometryColor: colors.primary[400],
          },
        });

        if (data?.updateLocation.success === true) {
          refetchLocationGeometry();
          setState((state) => ({
            ...state,
            editingLocation: null,
            mode: "view",
          }));

          toast.success(
            `Successfully updated ${truncateString(
              state.editingLocation.name,
              10,
            )}`,
          );

          setLocationPolygonsEnabled(true);
          return;
        } else {
          toast.error(
            `Failed to update ${truncateString(
              state.editingLocation.name,
              10,
            )}`,
          );
        }
      }

      // Necesary to narrow down the Geojson type
      const typedFeatures = featureCollection.features.filter(
        (feature): feature is Feature<Polygon | MultiPolygon> =>
          feature.geometry.type === "Polygon" ||
          feature.geometry.type === "MultiPolygon",
      );

      const typedFeatureCollection = {
        ...featureCollection,
        features: typedFeatures,
      };

      // Converts the polygons into multipolygons
      const combined = turf.combine(typedFeatureCollection);
      const coordinates = combined.features[0]?.geometry.coordinates;
      const latestDrawing = coordinates?.at(-1)?.flat();

      const currentMode = drawRef.current.getMode();

      if (latestDrawing == null || latestDrawing.length - 1 < 3) {
        // We minus 1 because the the first and the coordinates point are duplicated, even for a line
        toast.error("Polygon must have at least 3 points");
        return;
      }
      if (currentMode !== "simple_select" && currentMode !== "direct_select") {
        // If we're still partially drawing a polygon, we should not allow saving
        toast.error("Complete the polygon before saving");
        return;
      }

      const stringifiedGeometry = JSON.stringify(
        combined.features[0]?.geometry,
      );

      const { data } = await updateLocation({
        id: state.editingLocation.id,
        input: {
          geometry: stringifiedGeometry,
          geometryColor: colors.primary[400],
        },
      });

      if (data?.updateLocation.success !== true) {
        toast.error(
          `Failed to update ${truncateString(state.editingLocation.name, 10)}`,
        );
        return;
      }

      refetchLocationGeometry();
      setState((state) => ({
        ...state,
        editingLocation: null,
        mode: "view",
      }));

      toast.success(
        `Successfully updated ${truncateString(
          state.editingLocation.name,
          10,
        )}`,
      );
    }
  }, [
    drawRef,
    map,
    refetchLocationGeometry,
    setLocationPolygonsEnabled,
    state.editingLocation,
    updateLocation,
  ]);

  const confirmEdit = useCallback(async () => {
    if (map && state.editingMaterial != null) {
      const newCoords = map.getCenter();

      await updateMaterial.mutateAsync({
        materialId: state.editingMaterial.id,
        updates: {
          position: { latitude: newCoords.lat, longitude: newCoords.lng },
        },
      });

      setState((state) => ({
        ...state,
        editingMaterial: null,
        mode: "view",
      }));
    }
  }, [map, state.editingMaterial, updateMaterial]);

  const closeCoordinateMenu = useCallback(() => {
    setState((state) => ({ ...state, coordinateMenuProps: null }));
  }, []);
  const openCoordinateMenu = useCallback((props: CoordinateMenuProps) => {
    setState((state) => ({ ...state, coordinateMenuProps: props }));
  }, []);

  const setInitialView = useCallback(() => {
    setState((state) => ({ ...state, mode: "setInitialView" }));
  }, []);
  const confirmInitialView = useCallback(
    async (siteId: string) => {
      if (map == null) {
        toast.error("Failed to set initial view, map not loaded");
        return;
      }
      const center = map.getCenter();
      const zoom = map.getZoom();
      await updateSite({
        id: siteId,
        updates: {
          mapCenterLongitude: center.lng,
          mapCenterLatitude: center.lat,
          mapZoomLevel: zoom,
        },
      });
      refetchSite();
      setState((state) => ({ ...state, mode: "view" }));
    },
    [map, updateSite, refetchSite],
  );
  const openMissingInitialView = useCallback(() => {
    setShownNoInitialView(true);
    if (!shownNoInitialView) {
      setState((state) => ({ ...state, mode: "noInitialView" }));
    }
  }, [shownNoInitialView]);

  useEffect(() => {
    if (isMobile && !initialized) {
      setInitialized(true);
      setState((state) => ({
        ...state,
        sidebarOpen:
          matchedRoute.route === "material" ||
          matchedRoute.route === "location",
      }));
    } else if (isMobile && initialized && matchedRoute.route === "map") {
      setState((state) => ({
        ...state,
        sidebarOpen: false,
      }));
    }
  }, [initialized, isMobile, matchedRoute]);

  useMapAttribution();
  useMapResize(map);
  useMapPaste({ drawRef, mode: state.mode });

  return {
    ...search,
    ...state,
    matchedRoute,
    closeCoordinateMenu,
    openCoordinateMenu,
    collapseSidebar,
    expandSidebar,
    cancelEdit,
    cancelEditLocation,
    openFilters,
    closeFilters,
    confirmNewMaterial,
    cancelNewMaterial,
    openNewMaterial,
    requestEdit,
    requestLocationEdit,
    confirmEdit,
    confirmEditLocationGeometry,
    refetchLocationGeometry,
    setInitialView,
    confirmInitialView,
    openMissingInitialView,
  };
};

export const MapContext = createContext<
  ReturnType<typeof useMapStore> | undefined
>(undefined);

export const useMapContext = () => {
  const context = useContext(MapContext);
  if (context === undefined) {
    throw new Error("useMapContext must be used within a MapContext");
  }
  return context;
};
